/* eslint-disable camelcase */
import { KeyValueControl } from "@app-components/control/KeyValueControl";
import { TabularData } from "@app-components/TabularData";
import { useBroadcastChannel } from "@app-context/broadcastChannels/context";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  CenterContent,
  ContentMessage,
  SecondaryTabList,
  Spinner,
  TextInput,
  VisibleIf,
} from "@enfusion-ui/web-components";
import { useThisTab } from "@enfusion-ui/web-core";
import { faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import { fromPairs } from "lodash";
import queryString from "query-string";
import * as React from "react";
import ReactPrismEditor from "react-prism-editor";

import {
  InspectorHTTPConfig,
  useInspectorContext,
} from "../../context/inspector/context";
import {
  BodyContent,
  ContentProp,
  Line,
  ResponseContainer,
  ResponseTab,
} from "./InspectorContent";

const ResponseTabs = [
  {
    label: "Body",
    key: "body",
  },
  {
    label: "Headers",
    key: "headers",
  },
];

const getLang = (contentType: string | null) => {
  if (!contentType) return "text";
  if (contentType.includes("json")) return "json";
  if (contentType.includes("xml")) return "xml";
  if (contentType.includes("text/html")) return "html";
  return "text";
};

export const HTTPContent: React.FC<ContentProp> = ({
  selectedParamTab,
  paramsOpen,
}) => {
  const { config: configBase, setConfig } = useInspectorContext();

  const { id } = useThisTab();

  const [selectedMainTab, setSelectedMainTab] = React.useState<string>("body");

  const [loading, setLoading] = React.useState(false);
  const [response, setResponse] = React.useState<[Response, string] | null>(
    null
  );
  const [error, setError] = React.useState<Error | null>(null);
  const signalRef = React.useRef(new AbortController());

  const config = configBase as InspectorHTTPConfig;

  const handleSend = useRefCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setResponse(null);
      signalRef.current = new AbortController();
      const res = await fetch(
        queryString.stringifyUrl({
          url: config.path,
          query: config.params,
        }),
        {
          body:
            config.method !== "GET" && config.method !== "HEAD"
              ? config.body
              : undefined,
          method: config.method,
          headers: config.headers,
          signal: signalRef.current.signal,
        }
      );

      if (res.headers.get("Content-Type")?.startsWith("application/json")) {
        setResponse([res, JSON.stringify(await res.json(), null, 2)]);
      } else {
        setResponse([res, await res.text()]);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [config]);

  useBroadcastChannel("inspector-channel", (msg) => {
    const [msgId, type] = msg.split("<=>");
    if (msgId === id) {
      switch (type) {
        case "send":
          handleSend();
          break;
        case "cancel":
          signalRef.current.abort();
          setLoading(true);
          setError(null);
          setResponse(null);
          break;
        default:
          break;
      }
    }
  });

  const handleBodyContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) setConfig({ ...config, body: e.target.value });
  };

  const handleParamsChange = useRefCallback(
    (newParams: Array<[string, string]>) => {
      setConfig({ ...config, params: fromPairs(newParams) });
    },
    [config, setConfig]
  );

  const handleHeadersChange = useRefCallback(
    (newHeaders: Array<[string, string]>) => {
      setConfig({ ...config, headers: fromPairs(newHeaders) });
    },
    [config, setConfig]
  );

  const lang = React.useMemo(
    () => getLang(response ? response[0].headers.get("content-type") : null),
    [response]
  );

  return (
    <>
      <VisibleIf
        visibleStyle={{
          padding: "var(--spacing-xl)",
          height: 200,
          overflow: "auto",
        }}
        visible={paramsOpen && selectedParamTab !== "auth-token"}
      >
        <VisibleIf visible={selectedParamTab === "params"}>
          <KeyValueControl
            defaultValues={Object.entries(config.params)}
            onChange={handleParamsChange}
          />
        </VisibleIf>
        <VisibleIf visible={selectedParamTab === "headers"}>
          <KeyValueControl
            defaultValues={Object.entries(config.headers)}
            onChange={handleHeadersChange}
          />
        </VisibleIf>
        <VisibleIf visible={selectedParamTab === "body"}>
          <TextInput
            name="text"
            hideLabel
            value={config.body}
            onChange={handleBodyContentChange}
            textarea
            style={{ height: "180px" }}
          />
        </VisibleIf>
      </VisibleIf>
      <Line />
      <ResponseTab>
        {loading ? (
          <CenterContent>
            <Spinner />
          </CenterContent>
        ) : error ? (
          <CenterContent>
            <ContentMessage message={error.message} />
          </CenterContent>
        ) : response ? (
          <>
            <SecondaryTabList
              value={selectedMainTab}
              onSelect={setSelectedMainTab}
              tabs={ResponseTabs}
            />
            <ResponseContainer>
              <VisibleIf visible={selectedMainTab === "body"}>
                {lang === "text" ? (
                  <BodyContent>{response?.[1]}</BodyContent>
                ) : (
                  <ReactPrismEditor
                    language={lang}
                    theme="tomorrow"
                    code={response[1]}
                    lineNumber
                    readOnly
                    clipboard
                    showLanguage
                  />
                )}
              </VisibleIf>
              <VisibleIf visible={selectedMainTab === "headers"}>
                <TabularData
                  colDefs={[{ field: "name" }, { field: "value" }]}
                  rowData={[
                    ["url", response[0].url],
                    ["type", response[0].type],
                    ["status", response[0].status],
                    ["status text", response[0].statusText],
                    ["redirected", response[0].redirected],
                    ...response[0].headers.entries(),
                  ].map((e, idx) => ({
                    __row_id: idx,
                    name: e[0],
                    value: e[1],
                  }))}
                />
              </VisibleIf>
            </ResponseContainer>
          </>
        ) : (
          <CenterContent>
            <ContentMessage icon={faPaperPlane} message="Send Request" />
          </CenterContent>
        )}
      </ResponseTab>
    </>
  );
};
